import { Assert } from "@seriously-rad/seriouslyrad-framework-core/Assert";
import { DataSource } from "@seriously-rad/seriouslyrad-framework-core/Data/DataSource";
import { AndExpression } from "@seriously-rad/seriouslyrad-framework-core/Expressions/AndExpression";
import { ComparisonExpression } from "@seriously-rad/seriouslyrad-framework-core/Expressions/ComparisonExpression";
import { PartialNullable, RadPromiseLike } from "@seriously-rad/seriouslyrad-framework-core/Extensions";
import { ArrayHelper } from "@seriously-rad/seriouslyrad-framework-core/Helpers/ArrayHelper";
import { StringHelper } from "@seriously-rad/seriouslyrad-framework-core/Helpers/StringHelper";
import { Property } from "@seriously-rad/seriouslyrad-framework-core/RadObject";
import { WebApiClientService } from "@seriously-rad/seriouslyrad-framework-core/Server/WebApiClientService";
import { ServiceManager } from "@seriously-rad/seriouslyrad-framework-core/ServiceManager";
import { ButtonStyleEnum } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ButtonStyleEnum";
import { ComboBox } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ComboBox";
import { IControl } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/Control";
import { ControlRef } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ControlRef";
import { Date } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/Date";
import { InputControl } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/InputControl";
import { Label } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/Label";
import { Panel } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/Panel";
import { IParameterDialog, ParameterDialog, ParameterDialogPropertyValues } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ParameterDialog";
import { TextBox } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/TextBox";
import { NAMESPACE } from "./_Namespace";

export type ReportFilterDialogPropertyValues =
    ParameterDialogPropertyValues
    & {
    };

export type ReportFilterDialogProperties = {
    [name in keyof ReportFilterDialogPropertyValues]: Property<ReportFilterDialogPropertyValues[name]>
};

export interface IReportFilterDialog extends IParameterDialog, ReportFilterDialogProperties
{
}

export class ReportFilterDialog extends ParameterDialog implements IReportFilterDialog
{
    // #region Static

    public static override classNamespace = NAMESPACE + "ReportFilterDialog";
    public static override className = "ReportFilterDialog";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<ReportFilterDialogPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                buttonStyle: ButtonStyleEnum.okCancel,
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected parameters?: { control: IControl, inputControl: InputControl, parameterName: string }[];

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<ReportFilterDialogPropertyValues>)
    {
        super(propertyValues);

        this.parameters = [];
    }

    // #endregion

    // #region Public Methods

    public createParameters()
    {
        Assert.isNotNullOrUndefined(this.parameters, "this.parameters");

        const
            parameters: { name: string, value: string }[] = [];

        this.parameters.forEach(item =>
        {
            let
                value = item.inputControl.value.get();

            if (value && typeof value !== "string")
                value = value.toString();

            parameters.push({ name: item.parameterName, value: value });
        });

        return parameters;
    }

    // #endregion

    // #region Protected Methods

    protected _setupParameterDateAsync(data: any)
    {
        return new Promise(resolve =>
        {
            const
                panel = new Panel(),
                date = new Date().appendTo(panel);

            new Label({ text: data.displayName, associatedControlRef: new ControlRef(date) }).prependTo(panel);
            resolve([panel, date, data.name]);
        });
    }
    protected _setupParameterEntityAsync(parameter: { displayName: string, entityDescriptionProperty: string, entityId: string })
    {
        const
            panel = new Panel(),
            comboBox = new ComboBox().appendTo(panel);

        new Label({ text: parameter.displayName, minWidth: 100, associatedControlRef: new ControlRef(comboBox) }).prependTo(panel);

        return (
            ServiceManager.getRequired(WebApiClientService)
                .createEntitySearchRequest(
                    "Entities",
                    {
                        columns: ["namePlural"],
                        filter: new ComparisonExpression({ path: "id", value: parameter.entityId }),
                    }
                )
                .executeAsync()
                .then(data =>
                {
                    if (data?.length)
                    {
                        return (
                            ServiceManager.getRequired(WebApiClientService)
                                .createEntitySearchRequest(
                                    data[0].namePlural,
                                    {
                                        columns: ["id", StringHelper.toLowerCamelCase(parameter.entityDescriptionProperty)],
                                        orderBy: "name",
                                    }
                                )
                                .executeAsync()
                        );
                    }

                    return Promise.reject();
                })
                .then((listData: any[]) =>
                {
                    comboBox.listDataSource.set(
                        new DataSource({
                            data:
                                listData.map(item => ({ value: item.id, description: item[StringHelper.toLowerCamelCase(parameter.entityDescriptionProperty)] }))
                        })
                    );
                })
        );
    }
    protected _setupParametersAsync(data: any[])
    {
        if (!Array.isArray(data) || data.length === 0)
            return Promise.resolve();

        const
            promises: RadPromiseLike<any>[] = [];

        ArrayHelper.orderBy(
            data,
            item => item.order
        ).forEach(item =>
        {
            // TODO: Registration Architecture
            switch (item.type)
            {
                case "0d6f15aa-bc70-445f-bfe1-d4cef809ce25":
                    promises.push(this._setupParameterTextAsync(item));
                    break;
                case "24621a8e-0c90-4ad9-9a1d-9c0b6a0ece2b":
                    promises.push(this._setupParameterDateAsync(item));
                    break;
                case "35b2d1d4-0374-4da2-80e2-1e56a65f77e5":
                    promises.push(this._setupParameterEntityAsync(item));
                    break;
                default:
                    return Promise.reject(StringHelper.format("Unknown report filter parameter type: {0}", item.type));
            }
        });

        return (
            Promise
                .all(promises)
                .then(results =>
                {
                    Assert.isNotNullOrUndefined(this.parameters, "this.parameters");

                    for (let i = 0; i < results.length; i++)
                    {
                        results[i][0].appendTo(this._containerElementRef.current);
                        this.parameters.push({
                            control: results[i][0],
                            inputControl: results[i][1],
                            parameterName: results[i][2]
                        });
                    }
                })
        );
    }
    protected _setupParameterTextAsync(data: any)
    {
        return new Promise(resolve =>
        {
            const
                panel = new Panel(),
                textBox = new TextBox().appendTo(panel);

            new Label({ text: data.displayName, minWidth: 100, associatedControlRef: new ControlRef(textBox) }).prependTo(panel);
            resolve([panel, textBox, data.name]);
        });
    }

    // #endregion

    // #region Overrides

    public override controlsClear()
    {
        super.controlsClear();
        this.parameters = [];
    }
    public override createExpression()
    {
        Assert.isNotNullOrUndefined(this.parameters, "this.parameters");

        const
            expression = new AndExpression();

        this.parameters.forEach(item =>
        {
            let
                value = item.inputControl.value.get();

            if (value && typeof value !== "string")
                value = value.toString();

            expression.add(new ComparisonExpression({ path: item.parameterName, value: value }));
        });

        return expression.count() > 0 ? expression : null;
    }
    public override async openAsync(reportFilterId: string)
    {
        this.controlsClear();

        const
            data =
                await ServiceManager.getRequired(WebApiClientService)
                    .createEntitySearchRequest(
                        "ReportFilterParameters",
                        {
                            columns: ["order", "name", "displayName", "type", "entityId", "entityDescriptionProperty"],
                            filter: new ComparisonExpression({ path: "reportFilterId", value: reportFilterId }),
                        }
                    )
                    .executeAsync();

        await this._setupParametersAsync(data);
        return await super.openAsync();
    }

    // #endregion

    // #endregion
}

